<template>
  <FeedbackTopicSetting
    :is-active="isActive"
    :label="label"
    :icon="iconForPerspective(perspective)"
  >
    <BaseIconButton icon="remove" color="tonal" size="XS" @click="emits('minus')" />
    <input
      :id="inputId"
      type="number"
      min="0"
      step="1"
      :aria-label="label"
      class="border-outline-variant text-headline-small [appearance:textfield] mx-1 h-8 w-14 shrink-0 rounded-small border px-1 text-center [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      :value="draft"
      @input="onInput"
      @blur="onDraftBlur"
    />
    <BaseIconButton icon="add" color="tonal" size="XS" @click="emits('plus')" />
  </FeedbackTopicSetting>
</template>
<script lang="ts" setup>
  import { useId, watch, ref } from 'vue'
  import FeedbackTopicSetting from './FeedbackTopicSetting.vue'
  import { iconForPerspective } from '@/core/models/feedback-perspective'
  import type { Perspective } from '@/core/models/perspective'
  import BaseIconButton from '@/core/components/base/button/icon-button/BaseIconButton.vue'

  interface Props {
    isActive: boolean
    perspective: Perspective | string
    numberOfFeedbacks: number
    label: string
  }

  const props = defineProps<Props>()
  const emits = defineEmits<{
    (e: 'plus'): void
    (e: 'minus'): void
    (e: 'update:numberOfFeedbacks', value: number): void
  }>()

  const inputId = useId()
  const draft = ref(String(props.numberOfFeedbacks))

  watch(
    () => props.numberOfFeedbacks,
    (n) => {
      draft.value = String(n)
    },
  )

  const parseAndClamp = (raw: string): number | null => {
    const n = Number.parseInt(raw, 10)
    if (Number.isNaN(n)) {
      return null
    }
    return Math.max(0, n)
  }

  const onInput = (e: Event) => {
    draft.value = (e.target as HTMLInputElement).value
    const v = parseAndClamp(draft.value)
    if (v !== null) {
      emits('update:numberOfFeedbacks', v)
    }
  }

  const onDraftBlur = () => {
    const v = parseAndClamp(draft.value)
    const next = v === null ? props.numberOfFeedbacks : v
    draft.value = String(next)
    emits('update:numberOfFeedbacks', next)
  }
</script>
